import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import pDebounce from 'p-debounce'
import { connect } from 'react-redux'

import MuiAutoComplete, {
  CATEGORY,
} from 'ggx-componentlibrary/components/autocomplete/mui-autocomplete/mui-autocomplete.component'
import { OPTION_TYPE } from 'ggx-componentlibrary/components/autocomplete/mui-autocomplete/Options/options.component'
import { apiService } from '../../../api/api.service'
import { deliverySetOrderDetailImageUrl } from '../../../duck/actions'

const reduxFormPropTypeShape = PropTypes.shape({
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
})

const propTypes = {
  info: reduxFormPropTypeShape,
  length: reduxFormPropTypeShape,
  width: reduxFormPropTypeShape,
  height: reduxFormPropTypeShape,
  weight: reduxFormPropTypeShape,
  remark: reduxFormPropTypeShape,
  image: reduxFormPropTypeShape,
  size_option: reduxFormPropTypeShape,
  dispatchSetImageDataUrl: PropTypes.func,
}

const formatOptions = orders => {
  return orders.slice(0, 5).map(order => {
    return {
      groupType: OPTION_TYPE.PACKAGE_DETAILS,
      label: order.info,
      value: order,
    }
  })
}

const PackageDetailsAutoComplete = ({
  info = {},
  length = {},
  width = {},
  height = {},
  weight = {},
  remark = {},
  image = {},
  size_option = {},
  placeholder = "",
  ...props
}) => {
  const { touched, error } = info.meta || {}
  const { value: infoValue } = info.input || {}
  const autocompleteValue = { label: infoValue, value: infoValue }
  const [defaultOptions, setDefaultOptions] = useState([])
  const debouncedGetOrders = useMemo(
    () => pDebounce(apiService.getOrders, 300),
    []
  )

  useEffect(() => {
    debouncedGetOrders()
      .then(data => {
        setDefaultOptions(formatOptions(data.data.orders))
      })
      .catch(() => null)
  }, [])

  const handleQuery = newValue => {
    return debouncedGetOrders({ info: newValue })
      .then(data => {
        return formatOptions(data.data.orders)
      })
      .catch(err => {
        console.warn(
          'PackageDetailsAutoComplete getOrders failed => searchText: ',
          newValue
        )
        console.warn(
          'PackageDetailsAutoComplete getOrders failed => err: ',
          err
        )
      })
  }

  const handleInputChange = searchText => {
    const { onChange } = info.input || {}

    if (typeof onChange === 'function') {
      onChange(searchText)
    }
  }

  const handleChange = newValueObj => {
    const { onChange: infoOnChange } = info.input || {}
    const { onChange: lengthOnChange } = length.input || {}
    const { onChange: widthOnChange } = width.input || {}
    const { onChange: heightOnChange } = height.input || {}
    const { onChange: weightOnChange } = weight.input || {}
    const { onChange: remarkOnChange } = remark.input || {}
    const { onChange: sizeOptionOnChange } = size_option.input || {}

    if (newValueObj === null) {
      infoOnChange(null)
      return
    }

    const {
      info: newInfo = null,
      length: newLength = null,
      width: newWidth = null,
      height: newHeight = null,
      weight: newWeight = null,
      remark: newRemarks = null,
      image: newImage = null,
    } = newValueObj.value || {}

    infoOnChange(newInfo)
    lengthOnChange(newLength)
    widthOnChange(newWidth)
    heightOnChange(newHeight)
    weightOnChange(newWeight)
    remarkOnChange(newRemarks)
    sizeOptionOnChange(6)
    props.dispatchSetImageDataUrl(newImage)
  }

  const handleBlur = ({ searchText }) => {
    const { onBlur } = info.input || {}

    if (typeof onBlur === 'function') {
      onBlur(searchText)
    }
  }

  return (
    <MuiAutoComplete
      category={CATEGORY.GENERIC}
      onInputChange={handleInputChange}
      onQuery={handleQuery}
      onChange={handleChange}
      onBlur={handleBlur}
      defaultOptions={defaultOptions}
      menuHeight={300}
      meta={{ touched, error }}
      selectedOption={autocompleteValue}
      supportEditSelectedValue
      placeholder={placeholder}
      dataTestid="delivery-order-package-details-autocomplete"
    />
  )
}

PackageDetailsAutoComplete.propTypes = propTypes

const mapDispatchToProps = dispatch => ({
  dispatchSetImageDataUrl: imageDataUrl => {
    dispatch(deliverySetOrderDetailImageUrl(imageDataUrl))
  },
})

const ConnectedPackageDetailsAutoComplete = connect(
  null,
  mapDispatchToProps
)(PackageDetailsAutoComplete)

export { ConnectedPackageDetailsAutoComplete }
